/**
 * App Shell — navigation (design §7.1).
 * Fixed 248 px sidebar (collapses to 72 px at 768–1279), compact topbar,
 * mobile bottom dock, ⌘K command palette.
 *
 * Layout.tsx composes: <Navbar>{main + Footer}</Navbar>
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import { AnimatePresence, m } from 'framer-motion';
import { Command } from "cmdk";
import {
  ChevronLeft,
  Download,
  FlaskConical,
  Home,
  LibraryBig,
  LogIn,
  LogOut,
  Plus,
  ScrollText,
  Search,
  SquarePen,
  Upload,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Z } from "@/lib/z-layers";
import { getProcedureDisplayName, localizedSpecialtyName } from "@/lib/catalog";
import { useArchive, useSurgeryIndex, useSurgeries, useProfile } from "@/lib/store";
import { isMainTab, shellHeaderMode, applyShellHeaderMode } from "@/lib/shell";
import { routeMeta } from "@/lib/route-meta";
import { matchesQuery, normalize } from "@/components/library/filters";
import {
  hashPatientId,
  isPatientIdHash,
  normalizePatientId,
} from "@/lib/patient-id";
import { useAuth } from "@/hooks/useAuth";
import { LOGIN_PATH } from "@/const";
import { initialsOf } from "@/lib/format";
import { BrandSeal } from "./BrandSeal";
import { SignOutDialog } from "./SignOutDialog";
import { TopbarActionsProvider, useTopbarActionsSlot } from "./TopbarActions";
import { FOCUS_RING } from "./focus";
import { prefetchRoute } from "@/lib/prefetch-routes";

/* ───────────────────── shell context (palette + profile) ───────────────────── */

interface ShellState {
  openPalette: () => void;
  closePalette: () => void;
  paletteOpen: boolean;
}

const ShellContext = createContext<ShellState | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useShell(): ShellState {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error("useShell must be used inside <Navbar>");
  return ctx;
}

/* ───────────────────── navigation ───────────────────── */

const NAV_ITEMS = [
  { path: "/", labelKey: "common.nav.dashboard", icon: Home, end: true },
  {
    path: "/library",
    labelKey: "common.nav.library",
    icon: LibraryBig,
    end: false,
  },
  // /procedures kept as a route but hidden from nav until the catalog UX is ready.
  {
    path: "/profile",
    labelKey: "common.nav.profile",
    icon: UserRound,
    end: false,
  },
  {
    path: "/new",
    labelKey: "common.nav.newSurgery",
    icon: SquarePen,
    end: false,
  },
  {
    path: "/report",
    labelKey: "common.nav.annualReport",
    icon: ScrollText,
    end: false,
  },
  {
    path: "/research",
    labelKey: "common.nav.research",
    icon: FlaskConical,
    end: false,
  },
] as const;

/* ───────────────────── sidebar ───────────────────── */

function Sidebar() {
  const { t, i18n } = useTranslation();
  const { archiveSummary, exportFile, importFile } = useArchive();
  const { profile } = useProfile();
  const { user, isAuthenticated, isLoading: authLoading, isSigningOut } = useAuth();
  const [signOutOpen, setSignOutOpen] = useState(false);
  const importInput = useRef<HTMLInputElement>(null);

  const onExport = () => {
    void exportFile().then((name) =>
      toast.success(t("common.archive.exported", { name })),
    );
  };

  const onImport = async (file: File | undefined) => {
    if (!file) return;
    try {
      const text = await file.text();
      const r = importFile(text);
      toast.success(
        r.discarded > 0
          ? t("common.archive.importCompleteDiscarded", {
              added: r.added,
              updated: r.updated,
              discarded: r.discarded,
            })
          : t("common.archive.importComplete", {
              added: r.added,
              updated: r.updated,
            })
      );
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : t("common.archive.importFailed")
      );
    }
  };

  return (
    <aside
      style={{ zIndex: Z.navigation }}
      className="fixed inset-y-0 left-0 hidden w-[72px] flex-col border-r border-linea bg-superficie md:flex xl:w-[248px] print:hidden"
    >
      {/* header: seal only at md–lg; wordmark at xl */}
      <div className="flex h-16 items-center justify-center border-b border-linea px-4 xl:justify-start xl:gap-3 xl:px-5">
        <BrandSeal
          className="h-10 w-10 xl:h-9 xl:w-9"
          title={t("common.brand.sealAlt")}
        />
        <div className="hidden leading-tight xl:block">
          <p className="font-serif text-[19px] font-medium text-tinta">
            Suturia
          </p>
          <p className="estilo-etiqueta text-[11px] tracking-[0.2em] text-tinta-3">
            {t("common.brand.tagline")}
          </p>
        </div>
      </div>

      {/* navigation */}
      <nav
        className="flex flex-col gap-1 px-3 pt-4"
        aria-label={t("common.nav.mainAria")}
      >
        {NAV_ITEMS.map(({ path, labelKey, icon: Icon, end }) => (
          <NavLink
            key={path}
            to={path}
            end={end}
            title={t(labelKey)}
            onPointerEnter={() => prefetchRoute(path)}
            onFocus={() => prefetchRoute(path)}
            className={({ isActive, isPending }) =>
              cn(
                "relative flex h-10 items-center gap-3 rounded-lg px-3 text-[14px] font-medium transition-colors duration-150",
                isActive || isPending
                  ? "bg-acento-tinte text-acento-tinta before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-full before:bg-acento"
                  : "text-tinta-2 hover:bg-superficie-2 hover:text-tinta"
              )
            }
          >
            <Icon
              size={18}
              strokeWidth={1.75}
              className="shrink-0"
              aria-hidden
            />
            <span className="hidden xl:inline">{t(labelKey)}</span>
          </NavLink>
        ))}
      </nav>

      {/* "Your archive" block */}
      <div className="mt-6 hidden px-5 xl:block">
        <div className="filete-editorial pt-4">
          <p className="text-[12px] leading-4 text-tinta-2">
            {t("common.archive.title")}
          </p>
          <p className="mt-1 font-mono text-[13px] leading-[18px] text-tinta tnum">
            {archiveSummary.procedures}{" "}
            {t("common.procedure", { count: archiveSummary.procedures })} ·{" "}
            {archiveSummary.years}{" "}
            {t("common.archive.year", { count: archiveSummary.years })}
          </p>
        </div>
      </div>

      {/* footer: account/profile + session + export/import */}
      <div className="mt-auto flex flex-col gap-1 border-t border-linea p-3">
        {authLoading ? (
          /* neutral skeleton while the session resolves */
          <div className="flex items-center gap-3 p-2" aria-hidden>
            <span className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-linea" />
            <span className="hidden min-w-0 flex-1 xl:block">
              <span className="block h-3 w-24 animate-pulse rounded bg-linea" />
              <span className="mt-1.5 block h-3 w-32 animate-pulse rounded bg-linea" />
            </span>
          </div>
        ) : (
          <Link
            to="/profile"
            title={t("common.nav.profile")}
            className="flex items-center gap-3 rounded-lg p-2 text-left transition-colors duration-150 hover:bg-superficie-2"
          >
            {isAuthenticated && user?.avatar ? (
              <img
                src={user.avatar}
                alt=""
                className="h-9 w-9 shrink-0 rounded-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-acento-tinte font-mono text-[13px] font-medium text-acento-tinta">
                {initialsOf(
                  isAuthenticated && user?.name ? user.name : profile.nombre
                )}
              </span>
            )}
            <span className="hidden min-w-0 xl:block">
              <span className="block truncate text-[14px] font-medium leading-5 text-tinta">
                {isAuthenticated
                  ? user?.name ||
                    profile.nombre ||
                    t("common.profile.fallbackName")
                  : profile.nombre || t("common.profile.setupName")}
              </span>
              <span className="block truncate text-[12px] leading-4 text-tinta-2">
                {localizedSpecialtyName(profile.especialidad, i18n.language) ||
                  t("common.profile.fallbackSpecialty")}
              </span>
            </span>
          </Link>
        )}
        {!authLoading &&
          (isAuthenticated ? (
            <button
              type="button"
              onClick={() => setSignOutOpen(true)}
              disabled={isSigningOut}
              title={t("common.auth.logOut")}
              className="flex h-11 w-full items-center justify-center gap-2.5 px-2 text-[14px] font-medium text-tinta-2 transition-colors duration-150 hover:bg-superficie-2 hover:text-tinta disabled:opacity-50 xl:justify-start xl:px-3"
            >
              <LogOut
                size={22}
                strokeWidth={1.75}
                className="shrink-0"
                aria-hidden
              />
              <span className="hidden xl:inline">
                {t("common.auth.logOut")}
              </span>
            </button>
          ) : (
            <Link
              to={LOGIN_PATH}
              title={t("common.auth.signIn")}
              className="flex h-10 items-center gap-3 rounded-lg px-3 text-[14px] font-medium text-acento-tinta transition-colors duration-150 hover:bg-acento-tinte"
            >
              <LogIn
                size={18}
                strokeWidth={1.75}
                className="shrink-0"
                aria-hidden
              />
              <span className="hidden xl:inline">
                {t("common.auth.signIn")}
              </span>
            </Link>
          ))}
        <button
          type="button"
          onClick={onExport}
          title={t("common.archive.export")}
          className="flex h-10 items-center gap-3 rounded-lg px-3 text-[14px] font-medium text-tinta-2 transition-colors duration-150 hover:bg-superficie-2 hover:text-tinta"
        >
          <Download
            size={18}
            strokeWidth={1.75}
            className="shrink-0"
            aria-hidden
          />
          <span className="hidden xl:inline">{t("common.archive.export")}</span>
        </button>
        <button
          type="button"
          onClick={() => importInput.current?.click()}
          title={t("common.archive.import")}
          className="flex h-10 items-center gap-3 rounded-lg px-3 text-[14px] font-medium text-tinta-2 transition-colors duration-150 hover:bg-superficie-2 hover:text-tinta"
        >
          <Upload
            size={18}
            strokeWidth={1.75}
            className="shrink-0"
            aria-hidden
          />
          <span className="hidden xl:inline">{t("common.archive.import")}</span>
        </button>
        <input
          ref={importInput}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={e => {
            void onImport(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>
      <SignOutDialog open={signOutOpen} onClose={() => setSignOutOpen(false)} />
    </aside>
  );
}

/* ───────────────────── topbar ───────────────────── */

function useModKeyHint() {
  return useMemo(() => {
    if (typeof navigator === "undefined") return "⌘K";
    const mac = /Mac|iPhone|iPad|iPod/i.test(
      navigator.platform || navigator.userAgent
    );
    return mac ? "⌘K" : "Ctrl+K";
  }, []);
}

function TopbarBackButton({ to, label }: { to: string; label: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={to}
      aria-label={t("common.topbar.backAria", { label })}
      className={cn(
        "flex h-10 max-w-[min(100%,11rem)] items-center gap-0.5 rounded-full pr-2 text-[14px] font-medium text-tinta transition-colors duration-150",
        "hover:bg-superficie-2/90 active:scale-95",
        FOCUS_RING
      )}
    >
      <ChevronLeft size={22} strokeWidth={1.75} aria-hidden />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function Topbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { openPalette } = useShell();
  const surgeryIndex = useSurgeryIndex();
  const modHint = useModKeyHint();
  const topbarActions = useTopbarActionsSlot();

  const routeContext = useMemo(() => {
    const m = /^\/(?:surgery|edit)\/([^/]+)$/.exec(location.pathname);
    const surgery = m ? surgeryIndex.find(c => c.id === m[1]) : undefined;
    return {
      folio: surgery?.folio,
      procedureName: surgery
        ? getProcedureDisplayName(surgery.categoria)
        : undefined,
    };
  }, [location.pathname, surgeryIndex]);

  const meta = routeMeta(t, location.pathname, routeContext);
  const nested = !isMainTab(location.pathname) && Boolean(meta.backTo);
  const showGlobalSearch = shellHeaderMode(location.pathname) === "full";

  // Sync before paint so --shell-header matches the live topbar height on the
  // same frame as route changes (useEffect would lag by a paint).
  useLayoutEffect(() => {
    applyShellHeaderMode(showGlobalSearch ? "full" : "compact");
  }, [showGlobalSearch]);

  return (
    <>
      <header
        style={{ zIndex: Z.toolbar }}
        data-shell={showGlobalSearch ? "full" : "compact"}
        className={cn(
          "fixed inset-x-0 top-0 border-b border-linea/60 bg-papel/92 pt-[env(safe-area-inset-top)] backdrop-blur-xl backdrop-saturate-150 print:hidden",
          "md:left-[72px] xl:left-[248px]"
        )}
      >
        <div className="flex h-11 items-center gap-2 px-3 sm:px-4">
          <div className="flex min-w-0 shrink-0 items-center">
            {nested && meta.backTo ? (
              <TopbarBackButton
                to={meta.backTo}
                label={meta.backLabel ?? meta.title}
              />
            ) : (
              <Link
                to="/"
                aria-label={t("common.topbar.homeAria")}
                className={cn(
                  "rounded-full transition-transform duration-150 hover:scale-105 active:scale-95",
                  FOCUS_RING
                )}
              >
                <BrandSeal className="h-8 w-8" />
              </Link>
            )}
          </div>

          {showGlobalSearch ? (
            <button
              type="button"
              onClick={openPalette}
              className={cn(
                "group flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-xl",
                "border border-linea-fuerte bg-superficie px-3 text-[13px] text-tinta-3",
                "shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]",
                "transition-[border-color,box-shadow,background-color,color,transform] duration-150",
                "hover:border-acento/45 hover:bg-papel hover:text-tinta-2 active:scale-[0.995]",
                "focus-visible:outline-none focus-visible:border-acento focus-visible:ring-[3px] focus-visible:ring-acento-tinte"
              )}
              aria-label={t("common.topbar.searchLibraryAria")}
            >
              <Search
                size={16}
                strokeWidth={1.75}
                className="shrink-0 text-tinta-3 transition-colors duration-150 group-hover:text-acento"
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-left">{t("common.topbar.searchShort")}</span>
              <kbd className="estilo-mono-etiqueta ml-auto hidden shrink-0 rounded-md border border-linea bg-papel px-1.5 py-0.5 text-[10px] text-tinta-3 md:inline">
                {modHint}
              </kbd>
            </button>
          ) : nested ? (
            <h1 className="min-w-0 flex-1 truncate text-center font-serif text-[16px] font-medium leading-tight text-tinta">
              {meta.title}
            </h1>
          ) : (
            <div className="min-w-0 flex-1" aria-hidden />
          )}

          <div className="flex shrink-0 items-center justify-end gap-0.5">
            {topbarActions}
          </div>
        </div>
      </header>

      <div
        aria-hidden
        className="h-[var(--shell-header)] shrink-0 print:hidden"
      />
    </>
  );
}

/* ───────────────────── mobile bottom dock (editorial paper) ───────────────────── */

function MobileBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const items = [
    { path: "/", label: t("common.nav.dashboard"), icon: Home, end: true },
    {
      path: "/library",
      label: t("common.nav.library"),
      icon: LibraryBig,
      end: false,
    },
    {
      path: "/report",
      label: t("common.nav.annualReport"),
      icon: ScrollText,
      end: false,
    },
    {
      path: "/profile",
      label: t("common.nav.profile"),
      icon: UserRound,
      end: false,
    },
  ] as const;

  const itemClass = (active: boolean) =>
    cn(
      "flex h-12 min-h-12 flex-1 items-center justify-center rounded-lg transition-colors duration-150",
      active ? "text-acento-tinta" : "text-tinta-3 hover:text-tinta-2"
    );

  return (
    <nav
      style={{ zIndex: Z.navigation }}
      className="pointer-events-none fixed inset-x-0 bottom-0 flex justify-center px-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] md:hidden print:hidden"
      aria-label={t("common.nav.mobileAria")}
    >
      <div className="pointer-events-auto flex w-full items-center gap-0.5 rounded-2xl border border-linea bg-superficie/95 px-1.5 py-1.5 shadow-reposo backdrop-blur-md">
        {items.slice(0, 2).map(({ path, label, icon: Icon, end }) => (
          <NavLink
            key={path}
            to={path}
            end={end}
            aria-label={label}
            title={label}
            onPointerDown={() => prefetchRoute(path)}
            onFocus={() => prefetchRoute(path)}
            className={({ isActive, isPending }) => itemClass(isActive || isPending)}
          >
            <Icon size={26} strokeWidth={1.75} aria-hidden />
          </NavLink>
        ))}

        <button
          type="button"
          onClick={() => navigate("/new")}
          onPointerDown={() => prefetchRoute("/new")}
          aria-label={t("common.topbar.newSurgeryAria")}
          className="mx-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-acento text-white shadow-reposo transition-all duration-150 hover:bg-acento-tinta active:scale-95"
        >
          <Plus size={26} strokeWidth={2} aria-hidden />
        </button>

        {items.slice(2).map(({ path, label, icon: Icon, end }) => (
          <NavLink
            key={path}
            to={path}
            end={end}
            aria-label={label}
            title={label}
            onPointerDown={() => prefetchRoute(path)}
            onFocus={() => prefetchRoute(path)}
            className={({ isActive, isPending }) => itemClass(isActive || isPending)}
          >
            <Icon size={26} strokeWidth={1.75} aria-hidden />
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/* ───────────────────── ⌘K command palette (§7.9) ───────────────────── */

function PaletteItem({
  children,
  onSelect,
}: {
  children: ReactNode;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] text-tinta data-[selected=true]:bg-acento-tinte"
    >
      {children}
    </Command.Item>
  );
}

/** Palette content; mounted only when opened so the search starts empty */
function PaletteContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { surgeries } = useSurgeries();
  const { exportFile } = useArchive();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onSurgeryDetail = location.pathname.startsWith("/surgery/");
  const [query, setQuery] = useState(() => {
    if (!location.pathname.startsWith("/surgery/")) return "";
    return new URLSearchParams(location.search).get("q") ?? "";
  });
  const [patientIdHash, setPatientIdHash] = useState<string | null>(null);
  const accountKey = user ? String(user.id) : null;

  const go = useCallback(
    (path: string) => {
      prefetchRoute(path);
      onClose();
      navigate(path);
    },
    [onClose, navigate]
  );

  /** While viewing a ficha, mirror the palette query into `?q=` so matches highlight live. */
  useEffect(() => {
    if (!onSurgeryDetail) return;
    const q = query.trim();
    const next = q
      ? `${location.pathname}?q=${encodeURIComponent(q)}`
      : location.pathname;
    const current = `${location.pathname}${location.search}`;
    if (current === next) return;
    navigate(next, { replace: true });
  }, [query, onSurgeryDetail, location.pathname, location.search, navigate]);

  // Same sealed-ID hashing as the library search bar.
  useEffect(() => {
    let cancelled = false;
    const raw = query.trim();
    if (!raw || !accountKey) {
      setPatientIdHash(null);
      return;
    }
    if (isPatientIdHash(raw)) {
      setPatientIdHash(raw);
      return;
    }
    if (normalizePatientId(raw).length < 5) {
      setPatientIdHash(null);
      return;
    }
    void hashPatientId(raw, accountKey).then((h) => {
      if (!cancelled) setPatientIdHash(h);
    });
    return () => {
      cancelled = true;
    };
  }, [query, accountKey]);

  const matchedSurgeries = useMemo(() => {
    const q = query.trim();
    const sorted = [...surgeries].sort(
      (a, b) => b.fecha.localeCompare(a.fecha) || b.folio.localeCompare(a.folio),
    );
    if (!q) return sorted.slice(0, 50);
    return sorted
      .filter((s) => matchesQuery(s, q, { patientIdHash }))
      .slice(0, 50);
  }, [surgeries, query, patientIdHash]);

  const qn = normalize(query.trim());
  const actionVisible = (label: string) => !qn || normalize(label).includes(qn);

  const actions = [
    {
      key: "new",
      label: t("common.nav.newSurgery"),
      icon: SquarePen,
      onSelect: () => go("/new"),
    },
    {
      key: "report",
      label: t("common.palette.goToReport"),
      icon: ScrollText,
      onSelect: () => go("/report"),
    },
    {
      key: "research",
      label: t("common.palette.goToResearch"),
      icon: FlaskConical,
      onSelect: () => go("/research"),
    },
    {
      key: "profile",
      label: t("common.nav.profile"),
      icon: UserRound,
      onSelect: () => go("/profile"),
    },
    {
      key: "export",
      label: t("common.archive.export"),
      icon: Download,
      onSelect: () => {
        void exportFile().then((name) => {
          onClose();
          toast.success(t("common.archive.exported", { name }));
        });
      },
    },
  ] as const;
  const visibleActions = actions.filter((a) => actionVisible(a.label));

  return (
    <Command label={t("common.palette.title")} shouldFilter={false} loop>
      <div className="flex items-center gap-2.5 border-b border-linea bg-papel px-4">
        <Search
          size={17}
          strokeWidth={1.75}
          className="shrink-0 text-acento"
          aria-hidden
        />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder={t("common.palette.placeholder")}
          className="h-14 w-full bg-transparent text-base text-tinta outline-none placeholder:text-tinta-3 md:text-[15px]"
          autoFocus
        />
      </div>
      <Command.List className="max-h-[320px] overflow-y-auto p-2">
        <Command.Empty className="px-3 py-8 text-center text-[13px] text-tinta-2">
          {t("common.palette.empty")}
        </Command.Empty>
        {visibleActions.length > 0 ? (
          <Command.Group heading={t("common.palette.actions")}>
            {visibleActions.map(({ key, label, icon: Icon, onSelect }) => (
              <PaletteItem key={key} onSelect={onSelect}>
                <Icon size={16} strokeWidth={1.75} aria-hidden /> {label}
              </PaletteItem>
            ))}
          </Command.Group>
        ) : null}
        {matchedSurgeries.length > 0 ? (
          <Command.Group heading={t("common.palette.procedures")}>
            {matchedSurgeries.map((c) => (
              <Command.Item
                key={c.id}
                value={`${c.id} ${c.folio} ${c.diagnostico}`}
                onSelect={() => {
                  const q = query.trim();
                  go(
                    q
                      ? `/surgery/${c.id}?q=${encodeURIComponent(q)}`
                      : `/surgery/${c.id}`,
                  );
                }}
                className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] text-tinta data-[selected=true]:bg-acento-tinte"
              >
                <span className="estilo-mono-etiqueta shrink-0 rounded border border-linea bg-superficie-2 px-1.5 py-0.5 text-tinta-2">
                  {c.folio}
                </span>
                <span className="truncate">{c.diagnostico}</span>
                <span className="ml-auto hidden shrink-0 text-[12px] text-tinta-3 sm:inline">
                  {getProcedureDisplayName(c.categoria)}
                </span>
              </Command.Item>
            ))}
          </Command.Group>
        ) : null}
      </Command.List>
    </Command>
  );
}

function CommandPalette() {
  const { paletteOpen, closePalette } = useShell();

  return (
    <AnimatePresence>
      {paletteOpen && (
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{ zIndex: Z.palette }}
          className="fixed inset-0 flex items-start justify-center bg-[rgba(32,40,42,0.32)] p-4 pt-[18vh] backdrop-blur-[4px]"
          onClick={closePalette}
          role="presentation"
        >
          <m.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-[560px] overflow-hidden rounded-card border border-linea bg-superficie shadow-popover"
            onClick={e => e.stopPropagation()}
          >
            <PaletteContent onClose={closePalette} />
          </m.div>
        </m.div>
      )}
    </AnimatePresence>
  );
}

/* ───────────────────── main component ───────────────────── */

export default function Navbar({ children }: { children: ReactNode }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (
      searchParams.get("onboarding") === "1" &&
      location.pathname !== "/profile"
    ) {
      navigate("/profile?onboarding=1", { replace: true });
    }
  }, [location.pathname, navigate, searchParams]);

  const shell = useMemo<ShellState>(
    () => ({
      openPalette: () => setPaletteOpen(true),
      closePalette: () => setPaletteOpen(false),
      paletteOpen,
    }),
    [paletteOpen]
  );

  // global keyboard shortcuts (§9): ⌘K palette · N new · I report · / search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        // A modal always wins: the palette never stacks over an open dialog.
        if (!document.querySelector('[role="dialog"]')) setPaletteOpen(v => !v);
        return;
      }
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.toLowerCase();
      if (key === "n") {
        e.preventDefault();
        navigate("/new");
      } else if (key === "i") {
        e.preventDefault();
        navigate("/report");
      } else if (e.key === "/") {
        e.preventDefault();
        if (!document.querySelector('[role="dialog"]')) setPaletteOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <ShellContext.Provider value={shell}>
      <TopbarActionsProvider>
        <div className="min-h-[100dvh]">
          <Sidebar />
          <div className="flex min-h-[100dvh] flex-col md:pl-[72px] xl:pl-[248px] print:pl-0">
            <Topbar />
            {children}
          </div>
          <MobileBar />
          <CommandPalette />
        </div>
      </TopbarActionsProvider>
    </ShellContext.Provider>
  );
}
